import { useState, useEffect } from 'react';
import { apiClient } from '@/lib/api';
import { UserBadge } from '@/lib/types';
import EmptyState from '@/components/ui/EmptyState';


export default function BadgeCollection() {
  const [badges, setBadges] = useState<UserBadge[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadBadges();
  }, []);

  const loadBadges = async () => {
    try {
      setLoading(true);
      const response = await apiClient.getUserBadges();
      
      if (response.success && response.data) {
        setBadges(response.data?.results);
      } else {
        setError(response.error || 'Failed to load badges');
      }
    } catch (err) {
      setError('Failed to load badges');
    } finally {
      setLoading(false);
    }
  };

  const getBadgeIcon = (criteria: string) => {
    switch (criteria) {
      case 'course_completed':
        return '🎓';
      case 'high_score':
        return '🏆';
      case 'first_lesson':
        return '🌟';
      case 'perfect_exam':
        return '💯';
      case 'streak_7_days':
        return '🔥';
      case 'streak_30_days':
        return '⚡';
      case 'helpful_student':
        return '🤝';
      case 'creative_tutor':
        return '🎨';
      default:
        return '🏅';
    }
  };

  const getBadgeColor = (criteria: string) => {
    switch (criteria) {
      case 'course_completed':
        return 'bg-green-100 text-green-800 border-green-300';
      case 'high_score':
        return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      case 'first_lesson':
        return 'bg-blue-100 text-blue-800 border-blue-300';
      case 'perfect_exam':
        return 'bg-purple-100 text-purple-800 border-purple-300';
      case 'streak_7_days':
        return 'bg-orange-100 text-orange-800 border-orange-300';
      case 'streak_30_days':
        return 'bg-red-100 text-red-800 border-red-300';
      case 'helpful_student':
        return 'bg-teal-100 text-teal-800 border-teal-300';
      case 'creative_tutor':
        return 'bg-pink-100 text-pink-800 border-pink-300';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-md p-4">
        <p className="text-red-800">{error}</p>
        <button 
          onClick={loadBadges}
          className="mt-2 text-red-600 hover:text-red-800 underline"
        >
          Try again
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-bold text-gray-900">My Badges</h2>
        <div className="text-sm text-gray-500">
          {badges.length} Badge{badges.length !== 1 ? 's' : ''} Earned
        </div>
      </div>

      {badges.length === 0 ? (
        <EmptyState
          icon="🏅"
          title="No badges earned yet."
          description="Complete courses, pass exams, and maintain learning streaks to earn badges!"
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {badges && badges?.map((userBadge) => (
            <div 
              key={userBadge.id} 
              className="bg-white rounded-lg shadow-md overflow-hidden border-2 border-gray-200 hover:shadow-lg transition-shadow"
            >
              <div className="p-6 text-center">
                {/* Badge Icon */}
                <div className="mb-4">
                  <div className="w-16 h-16 mx-auto bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-2xl">
                    {getBadgeIcon(userBadge.badge.criteria ?? '')}
                  </div>
                </div>

                {/* Badge Name */}
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {userBadge.badge.name}
                </h3>

                {/* Badge Description */}
                <p className="text-gray-600 text-sm mb-4">
                  {userBadge.badge.description}
                </p>

                {/* Criteria Badge */}
                {userBadge.badge.criteria && (
                <div className="mb-4">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getBadgeColor(userBadge.badge.criteria)}`}>
                    {userBadge.badge.criteria.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  </span>
                </div>
                )}

                {/* Awarded Date */}
                <div className="text-xs text-gray-500">
                  Awarded on {new Date(userBadge.awarded_at).toLocaleDateString()}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Available Badges Info */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h3 className="text-sm font-medium text-blue-900 mb-3">Available Badges:</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm text-blue-800">
          <div className="flex items-center space-x-2">
            <span>🎓</span>
            <span>Course Completion</span>
          </div>
          <div className="flex items-center space-x-2">
            <span>🏆</span>
            <span>High Score</span>
          </div>
          <div className="flex items-center space-x-2">
            <span>🌟</span>
            <span>First Lesson</span>
          </div>
          <div className="flex items-center space-x-2">
            <span>💯</span>
            <span>Perfect Exam</span>
          </div>
          <div className="flex items-center space-x-2">
            <span>🔥</span>
            <span>7-Day Streak</span>
          </div>
          <div className="flex items-center space-x-2">
            <span>⚡</span>
            <span>30-Day Streak</span>
          </div>
        </div>
      </div>
    </div>
  );
} 